import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Printer, ScanBarcode, Search } from "lucide-react";
import { LABEL_TEMPLATES, printBarcodeLabels } from "@/lib/printLabels";

const AR_LABEL = { small: "صغير", medium: "متوسط", large: "كبير" };

/** Bulk-print A4 barcode labels for warehouse. Multi-select products + qty each + size template. */
export default function BulkLabels() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({}); // {product_id: qty}
  const [tplKey, setTplKey] = useState(() => localStorage.getItem("label_tpl") || "medium");

  useEffect(() => { api.get("/products").then(r => setProducts(r.data)).catch(e => toast.error(formatErr(e))); }, []);
  useEffect(() => { localStorage.setItem("label_tpl", tplKey); }, [tplKey]);

  const tpl = LABEL_TEMPLATES[tplKey] || LABEL_TEMPLATES.medium;

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return products.filter(p => !t || p.name.toLowerCase().includes(t) || (p.barcode || "").includes(q)).slice(0, 200);
  }, [products, q]);

  const setQty = (pid, val) => setSelected(s => ({...s, [pid]: Math.max(0, +val || 0)}));
  const total = Object.values(selected).reduce((s, v) => s + v, 0);

  const printAll = () => {
    // Expand each product by its selected quantity, then send to shared print helper.
    const items = products
      .filter(p => (selected[p.id] || 0) > 0)
      .flatMap(p => Array(selected[p.id]).fill(p));
    if (items.length === 0) return toast.error("اختر صنفاً واحداً على الأقل");
    const res = printBarcodeLabels(items, tplKey);
    if (res.ok) toast.success(`جاري تجهيز ${res.count} ملصق (${tpl.label})`);
    else if (res.reason === "popup_blocked") toast.error("النوافذ المنبثقة محجوبة — فعّل السماح للموقع");
  };

  const bulkFill = (n) => {
    const map = {};
    filtered.forEach(p => { map[p.id] = n; });
    setSelected(map);
  };

  return (
    <div className="space-y-6" data-testid="bulk-labels-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><ScanBarcode className="w-6 h-6 text-primary" /> طباعة ملصقات باركود</h1>
          <p className="text-sm text-muted-foreground mt-1">اختر عدد الملصقات لكل صنف، ثم اطبع على ورق A4</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1 border rounded-lg p-1 bg-muted/40" data-testid="label-tpl-group">
            {Object.values(LABEL_TEMPLATES).map(t => (
              <button
                key={t.key}
                onClick={() => setTplKey(t.key)}
                data-testid={`tpl-${t.key}-btn`}
                className={`px-3 py-1.5 text-xs rounded-md transition ${tplKey === t.key ? "bg-primary text-primary-foreground font-semibold shadow-sm" : "hover:bg-accent"}`}
              >
                {AR_LABEL[t.key]} {t.label}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={()=>bulkFill(1)} data-testid="fill-1-btn">تعبئة الكل بـ 1</Button>
          <Button variant="outline" onClick={()=>setSelected({})} data-testid="clear-btn">مسح</Button>
          <Button onClick={printAll} className="gap-1" data-testid="print-labels-btn"><Printer className="w-4 h-4" /> طباعة {total > 0 ? `(${total})` : ""}</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        <span data-testid="tpl-info">القالب الحالي: <b className="text-foreground">{tpl.label} مم</b> · {tpl.cols} أعمدة/صفحة</span>
        <div
          className="flex flex-col items-center justify-between text-center bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 shadow-sm rounded p-1"
          style={{ width: `${tpl.w * 2.4}px`, height: `${tpl.h * 2.4}px` }}
          data-testid="tpl-preview"
          title={`معاينة ${tpl.label} مم`}
        >
          <div className="font-bold truncate w-full" style={{ fontSize: `${tpl.nameFs + 1}px`, color: "#0f172a" }}>عينة صنف</div>
          <div className="flex items-center justify-center w-full" style={{ height: `${tpl.barH * 0.8}px` }}>
            <div className="flex items-end gap-[1px] h-full w-full justify-center">
              {/* Fake barcode bars */}
              {Array.from({length: 22}).map((_, i) => (
                <div key={i} className="bg-slate-900 dark:bg-slate-100" style={{ width: `${(i % 3) + 1}px`, height: "100%" }} />
              ))}
            </div>
          </div>
          <div className="font-bold tabular-nums text-blue-600" style={{ fontSize: `${tpl.priceFs + 1}px` }}>3,500 SYP</div>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="بحث بالاسم أو الباركود..." className="ps-9" data-testid="labels-search-input" />
      </div>

      <Card className="p-2">
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
          {filtered.map(p => (
            <div key={p.id} className="flex items-center gap-3 p-3 hover:bg-accent" data-testid={`label-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{p.barcode || "—"} · {p.sale_price?.toLocaleString?.("ar-EG")} {p.currency}</div>
              </div>
              <Input type="number" min={0} max={200} value={selected[p.id] || 0} onChange={(e)=>setQty(p.id, e.target.value)} className="w-20 h-9 text-center" data-testid={`qty-${p.id}`} />
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center text-muted-foreground py-8 text-sm">لا توجد نتائج</div>}
        </div>
      </Card>
    </div>
  );
}
